import React, { useState, useEffect, useContext } from "react";
import { CoreContext } from "../../context/global/CoreContext";

// API
import { getUnsignedTimesheets } from "../../services/api/timekeeping";

// Icons
import { ChevronRight } from "../../components/icons/svg/outline";

// Components
import { Spinner } from "../../components/ui";

// Utility functions
import { formatDate } from "../../utils/datetime";

const TimesheetList = () => {
  const { user, appName } = useContext(CoreContext);
  const [unsignedTimesheets, setUnsignedTimesheets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate API call
    const fetchUnsignedTimesheets = async () => {
      setIsLoading(true);
      try {
        // TODO: Replace with actual API call when backend is ready
        const response = await getUnsignedTimesheets();
        setUnsignedTimesheets(response.details || []);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching unsigned timesheets:", error);
        setIsLoading(false);
      }
    };
    fetchUnsignedTimesheets();
  }, []);

  const handleTimesheetSelect = (workerID, weekEnding) => {
    // Open SuperTimesheet in a new tab
    window.open(
      `/app/timekeeping/super-timesheet/${workerID}/${weekEnding}`,
      "_blank"
    );
  };

  // Set page title and app name
  React.useEffect(() => {
    document.title = `Timesheet Review`;
    if (appName) {
      appName.current = "Timesheet Review";
    }
  }, [user, appName]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <Spinner size="h-12 w-12" className="text-green-900" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 py-6 max-w-4xl">
      <div className="bg-white rounded-lg shadow-lg">
        <div className="bg-green-900 text-white p-2 px-4 font-bold rounded-t-lg">
          <h1 className="font-bold">Pending Timesheet Payout Reviews</h1>
        </div>

        <div className="px-6 py-2">
          {unsignedTimesheets.length === 0 ? (
            <div className="text-center py-6">
              <div className="text-green-900 text-6xl mb-4">✓</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                All Caught Up!
              </h3>
              <p className="text-gray-500">
                There are no pending timesheets that require your review.
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              <div className="text-sm text-gray-600 mb-2">
                {unsignedTimesheets.length} timesheet
                {unsignedTimesheets.length !== 1 ? "s" : ""} pending review
              </div>

              {unsignedTimesheets.map((timesheet, index) => {
                return (
                  <div
                    key={`${timesheet.worker}-${timesheet.week_ending}`}
                    className="border border-gray-200 rounded-lg p-1 px-2 hover:bg-gray-50 hover:border-green-900 transition-all duration-200 cursor-pointer group"
                    onClick={() =>
                      handleTimesheetSelect(
                        timesheet.worker,
                        timesheet.week_ending
                      )
                    }
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {timesheet.worker_name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Week ending:{" "}
                            {formatDate(
                              timesheet.week_ending,
                              "day month DD, YYYY"
                            )}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center space-x-3">
                        <span className="px-3 py-1 bg-yellow-50 group-hover:bg-green-50 transition-colors duration-200 text-black text-sm font-medium rounded-full">
                          Pending Review
                        </span>
                        <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-green-900 transition-colors duration-200" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimesheetList;
